import React, { useEffect, useRef, useState } from 'react'

import { TableContainer } from '@mui/material'

import StickyHorizontalScrollbar from './StickyHorizontalScrollbar'
import {
  tableDisplayToCssVars,
  type TableDensity,
  type TableFontSize
} from './tableDisplay'

interface Props {
  children: React.ReactNode
  fontSize?: TableFontSize
  density?: TableDensity
  /** Disable the floating horizontal scrollbar (defaults to enabled). */
  stickyScrollbar?: boolean
}

const isTransparent = (color: string) =>
  !color ||
  color === 'transparent' ||
  color.replace(/\s/g, '').startsWith('rgba(0,0,0,0)')

/**
 * Walk up from `el` until an element with an opaque background is found, so the
 * sticky actions column can paint the exact surface color behind the table
 * (rows themselves are transparent) and blend in instead of looking lighter.
 */
const resolveSurfaceColor = (el: HTMLElement | null): string => {
  let node: HTMLElement | null = el
  while (node) {
    const bg = window.getComputedStyle(node).backgroundColor
    if (!isTransparent(bg)) return bg
    node = node.parentElement
  }
  return '#fff'
}

/**
 * Wraps a table in its horizontal scroll container and adds:
 *  - the per-table display CSS variables (font size + density),
 *  - the resolved surface color (`--cb-sticky-bg`) for the sticky column, and
 *  - a floating horizontal scrollbar pinned to the viewport bottom.
 *
 * The TableContainer is the scroll element that sticky columns (`right: 0`) and
 * the proxy scrollbar reference.
 */
const ScrollableTableContainer: React.FC<Props> = ({
  children,
  fontSize,
  density,
  stickyScrollbar = true
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [stickyBg, setStickyBg] = useState<string>('#fff')

  useEffect(() => {
    setStickyBg(resolveSurfaceColor(wrapperRef.current))
  }, [])

  const cssVars = {
    ...tableDisplayToCssVars({ fontSize, density }),
    ['--cb-sticky-bg' as any]: stickyBg
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', ...cssVars }}>
      <TableContainer ref={scrollRef}>{children}</TableContainer>
      {stickyScrollbar && <StickyHorizontalScrollbar targetRef={scrollRef} />}
    </div>
  )
}

export default ScrollableTableContainer
